import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';

// Lazy-loaded page components for code splitting
const Dashboard = lazy(() => import('./pages/Dashboard'));
const MastersPage = lazy(() => import('./pages/Masters'));
const CustomerOrdersList = lazy(() => import('./pages/CustomerOrders/List'));
const StoragePage = lazy(() => import('./pages/Storage'));
const ProcurementsList = lazy(() => import('./pages/Procurement/List'));
const InboundList = lazy(() => import('./pages/Inbound/List'));
const PacksPage = lazy(() => import('./pages/Packs'));
const CreatePacksPage = lazy(() => import('./pages/Packs/components/CreatePacksPage'));
const DeliveryFormPage = lazy(() => import('./pages/Packs/components/DeliveryFormPage'));
const DeliveryPage = lazy(() => import('./pages/Delivery'));
const LoadingPage = lazy(() => import('./pages/Loading'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const AuditLogPage = lazy(() => import('./pages/AuditLog'));
const SettingsPage = lazy(() => import('./pages/Settings'));
const CreatePlanPage = lazy(() => import('./pages/MappingScheduling/CreatePlan'));
const ProductionPage = lazy(() => import('./pages/Production'));
const ProductionDetailPage = lazy(() => import('./pages/Production/PlanDetail'));
const ProductionPrintPage = lazy(() => import('./pages/Production/PlanPrintPage'));
const DataToolsPage = lazy(() => import('./pages/DataTools'));

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center h-64">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
  </div>
);



const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 2,
      staleTime: 30000,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <Suspense fallback={<PageLoader />}>
            <ErrorBoundary>
            <Routes>
              {/* Public route */}
              <Route path="/login" element={<LoginPage />} />

              {/* Protected routes */}
              <Route path="/" element={<ProtectedRoute><Layout><Dashboard /></Layout></ProtectedRoute>} />
              <Route path="/masters" element={<ProtectedRoute module="masters"><Layout><MastersPage /></Layout></ProtectedRoute>} />
              <Route path="/orders" element={<ProtectedRoute module="customer_orders"><Layout><CustomerOrdersList /></Layout></ProtectedRoute>} />
              <Route path="/storage" element={<ProtectedRoute module="storage"><Layout><StoragePage /></Layout></ProtectedRoute>} />
              <Route path="/packs" element={<ProtectedRoute module="packs"><Layout><PacksPage /></Layout></ProtectedRoute>} />
              <Route path="/packs/create" element={<ProtectedRoute module="packs"><Layout><CreatePacksPage /></Layout></ProtectedRoute>} />
              <Route path="/packs/deliver" element={<ProtectedRoute module="packs"><Layout><DeliveryFormPage /></Layout></ProtectedRoute>} />
              <Route path="/procurement" element={<ProtectedRoute module="procurement"><Layout><ProcurementsList /></Layout></ProtectedRoute>} />
              <Route path="/inbound" element={<ProtectedRoute module="inbound"><Layout><InboundList /></Layout></ProtectedRoute>} />
              <Route path="/delivery" element={<ProtectedRoute module="delivery"><Layout><DeliveryPage /></Layout></ProtectedRoute>} />
              <Route path="/loading" element={<ProtectedRoute module="loading"><Layout><LoadingPage /></Layout></ProtectedRoute>} />
              <Route path="/mapping-scheduling" element={<ProtectedRoute module="mapping_scheduling"><CreatePlanPage /></ProtectedRoute>} />
              {/* Production is stage 2 of Mapping & Scheduling — gated on the same module. */}
              <Route path="/production" element={<ProtectedRoute module="mapping_scheduling"><Layout><ProductionPage /></Layout></ProtectedRoute>} />
              <Route path="/production/:planId" element={<ProtectedRoute module="mapping_scheduling"><Layout><ProductionDetailPage /></Layout></ProtectedRoute>} />
              <Route path="/production/:planId/print" element={<ProtectedRoute module="mapping_scheduling"><Layout><ProductionPrintPage /></Layout></ProtectedRoute>} />
              <Route path="/audit-logs" element={<ProtectedRoute module="audit_logs"><Layout><AuditLogPage /></Layout></ProtectedRoute>} />
              <Route path="/data-tools" element={<ProtectedRoute module="data_tools"><Layout><DataToolsPage /></Layout></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute><Layout><SettingsPage /></Layout></ProtectedRoute>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </ErrorBoundary>
          </Suspense>
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
